import React, { useEffect, useRef } from 'react';
import { Box } from '@mui/material';
import { styled } from '@mui/material/styles';
import AIAdvantageFeature from './AIAdvantageFeature';
import FeaturesIntroSection from './FeaturesIntroSection';
import PerformanceDashboardSection from './PerformanceDashboardSection';
import LeaderboardFeature from './LeaderboardFeature';

const SectionContainer = styled(Box)(({ theme }) => ({
  backgroundColor: '#0f172a', // slate-900
  padding: '80px 24px',
  overflow: 'hidden',
  width: '100%',
  [theme.breakpoints.up('md')]: {
    padding: '128px 48px'
  },
  [theme.breakpoints.up('lg')]: {
    padding: '128px 64px'
  }
}));

const ContentWrapper = styled(Box)({
  width: '100%',
  margin: '0 auto'
});

const HeaderWrapper = styled(Box)(({ theme }) => ({
  maxWidth: '768px',
  margin: '0 auto',
  textAlign: 'center',
  marginBottom: '64px',
  opacity: 0,
  transform: 'translateY(20px)',
  transition: 'opacity 0.6s ease-out, transform 0.6s ease-out',
  '&.is-visible': {
    opacity: 1,
    transform: 'translateY(0)'
  },
  [theme.breakpoints.up('md')]: {
    marginBottom: '96px'
  }
}));

const Title = styled('h2')(({ theme }) => ({
  fontSize: '36px',
  fontWeight: 800,
  lineHeight: 1.2,
  letterSpacing: '-0.025em',
  color: '#ffffff',
  margin: 0,
  marginBottom: '16px',
  [theme.breakpoints.up('md')]: {
    fontSize: '48px'
  },
  [theme.breakpoints.up('lg')]: {
    fontSize: '60px'
  }
}));

const GradientText = styled('span')({
  background: 'linear-gradient(90deg, #22d3ee, #a5b4fc)',
  WebkitBackgroundClip: 'text',
  WebkitTextFillColor: 'transparent',
  backgroundClip: 'text',
  display: 'inline'
});

const Subtitle = styled('p')(({ theme }) => ({
  marginTop: '16px',
  fontSize: '16px',
  color: '#94a3b8', // slate-400
  margin: '0 auto',
  lineHeight: 1.6,
  maxWidth: '636px',
  [theme.breakpoints.up('md')]: {
    fontSize: '18px'
  }
}));

const FeaturesContainer = styled(Box)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  gap: '48px',
  width: '100%',
  [theme.breakpoints.up('md')]: {
    gap: '64px'
  }
}));

const OurFeaturesSection = () => {
  const headerRef = useRef(null);

  useEffect(() => {
    const observerOptions = {
      rootMargin: '0px',
      threshold: 0.1
    };

    const observer = new IntersectionObserver((entries) => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          entry.target.classList.add('is-visible');
          observer.unobserve(entry.target);
        }
      });
    }, observerOptions);

    if (headerRef.current) {
      observer.observe(headerRef.current);
    }

    return () => observer.disconnect();
  }, []);

  return (
    <SectionContainer>
      <ContentWrapper>
        {/* Section Header */}
        <HeaderWrapper ref={headerRef} className="animate-on-scroll">
          <Title>
            Our <GradientText>Features</GradientText>
          </Title>
          <Subtitle>
            Comprehensive tools designed for business owners who want results without complexity.
          </Subtitle>
        </HeaderWrapper>

        {/* Features Container */}
        <FeaturesContainer>
          <AIAdvantageFeature />
          <FeaturesIntroSection />
          <PerformanceDashboardSection />
          <LeaderboardFeature />
        </FeaturesContainer>
      </ContentWrapper>
    </SectionContainer>
  );
};

export default OurFeaturesSection;
